import React from 'react';
import { FaStar, FaRegStar } from 'react-icons/fa';

/**
 * StarRating Component
 * Displays a star rating with support for partial stars (e.g., 3.5 out of 5)
 * 
 * @param {number} rating - The rating value (0-5, can be decimal like 3.5)
 * @param {string} size - Size of stars: 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param {boolean} showNumber - Whether to show the numeric rating next to stars (default: false)
 * @param {boolean} showReviews - Whether to show review count (default: false)
 * @param {number} reviews - Number of reviews (required if showReviews is true)
 * @param {string} className - Additional CSS classes
 */
function StarRating({ 
  rating = 0, 
  size = 'md', 
  showNumber = false, 
  showReviews = false, 
  reviews = 0,
  className = '' 
}) {
  // Size mapping
  const sizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
    xl: 'h-6 w-6'
  };

  const starSize = sizes[size] || sizes.md;
  
  // Clamp rating between 0 and 5
  const clampedRating = Math.max(0, Math.min(5, rating));
  
  // Calculate full stars and partial star
  const fullStars = Math.floor(clampedRating);
  const hasPartialStar = clampedRating % 1 !== 0;
  const partialStarPercentage = hasPartialStar ? (clampedRating % 1) * 100 : 0;
  const emptyStars = 5 - fullStars - (hasPartialStar ? 1 : 0);

  return (
    <div className={`flex items-center gap-1 ${className}`} role="img" aria-label={`Rating: ${clampedRating.toFixed(1)} out of 5 stars`}>
      {/* Full stars */}
      {Array.from({ length: fullStars }).map((_, index) => (
        <FaStar
          key={`full-${index}`}
          className={`${starSize} text-yellow-400`}
          aria-hidden="true"
        />
      ))}
      
      {/* Partial star */}
      {hasPartialStar && (
        <div className={`${starSize} relative inline-block`} style={{ position: 'relative' }}>
          {/* Empty star background */}
          <FaRegStar
            className={`${starSize} text-gray-300`}
            aria-hidden="true"
          />
          {/* Filled portion overlay */}
          <div 
            className="absolute inset-0 overflow-hidden"
            style={{ 
              width: `${partialStarPercentage}%`,
              position: 'absolute',
              top: 0,
              left: 0
            }}
          >
            <FaStar
              className={`${starSize} text-yellow-400`}
              aria-hidden="true"
            />
          </div>
        </div>
      )}
      
      {/* Empty stars */}
      {Array.from({ length: emptyStars }).map((_, index) => (
        <FaRegStar
          key={`empty-${index}`}
          className={`${starSize} text-gray-300`}
          aria-hidden="true"
        />
      ))}
      
      {/* Optional: Show numeric rating */}
      {showNumber && (
        <span className={`ml-1 font-medium ${
          className.includes('text-white') ? 'text-white' : 
          className.includes('text-yellow-300') ? 'text-yellow-300' : 
          'text-gray-700'
        }`}>
          {clampedRating > 0 ? clampedRating.toFixed(1) : 'New'}
        </span>
      )}
      
      {/* Optional: Show review count */}
      {showReviews && reviews > 0 && (
        <span className={`ml-1 text-sm ${
          className.includes('text-white') ? 'text-white/90' : 
          className.includes('text-yellow-300') ? 'text-yellow-300/90' : 
          'text-gray-500'
        }`}>
          ({reviews} {reviews === 1 ? 'review' : 'reviews'})
        </span>
      )}
    </div>
  );
}

export default StarRating;

